import {
    FormEventHandler,
    ReactElement,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from 'react';
import { useUpdatePinAction } from '../actions';
import { ChangeEmailSectionFormRef } from './change-email-section';
import { useMeTranslation } from '../hooks';
import {
    UIFormInput,
    UIFormInputRef,
    UISection,
    UISectionContent,
    UISectionFooter,
    UISectionHeader,
    UIText
} from '@/ui';

export function ChangePinSection(): ReactElement {
    const formRef = useRef<ChangeEmailSectionFormRef>(null);

    const updatePin = useUpdatePinAction();

    function onSubmit(data: { newPin: string; currentPassword: string }) {
        const { newPin, currentPassword } = data;
        updatePin
            .updatePin({ pin: newPin, password: currentPassword })
            .then(() => {
                formRef.current?.resetForm();
            })
            .catch(() => {});
    }

    return (
        <ChangePinSectionForm
            ref={formRef}
            loading={updatePin.loading}
            onSubmit={onSubmit}
        />
    );
}

type ChangePinSectionFormProps = {
    loading: boolean;
    onSubmit: (data: { newPin: string; currentPassword: string }) => void;
};

export type ChangePinSectionFormRef = {
    resetForm: () => void;
};

export const ChangePinSectionForm = forwardRef<
    ChangePinSectionFormRef,
    ChangePinSectionFormProps
>((props, ref): ReactElement => {
    const { loading, onSubmit } = props;

    const { t } = useMeTranslation();

    const [showRealTimeErrors, setShowRealTimeErrors] = useState(false);

    /**
     * Current Password
     */
    const currentPasswordRef = useRef<UIFormInputRef>(null);
    const [currentPassword, setCurrentPassword] = useState('');
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const checkCurrentPassword = useCallback((password: string): string => {
        if (password.length === 0) {
            return 'Current password is required';
        }

        return '';
    }, []);
    useEffect(() => {
        if (showRealTimeErrors) {
            setCurrentPasswordError(checkCurrentPassword(currentPassword));
        }
    }, [currentPassword, showRealTimeErrors, checkCurrentPassword]);

    /**
     * New PIN
     */
    const newPinRef = useRef<UIFormInputRef>(null);
    const [newPin, setNewPin] = useState('');
    const [newPinError, setNewPinError] = useState('');
    const checkNewPin = useCallback((pin: string): string => {
        if (pin.length === 0) {
            return 'PIN is required';
        }
        if (pin.length < 4) {
            return 'PIN must be at least 4 digits';
        }
        if (pin.length > 4) {
            return 'PIN must be exactly 4 digits';
        }

        if (!/^\d+$/.test(pin)) {
            return 'PIN must contain only digits';
        }

        return '';
    }, []);
    useEffect(() => {
        if (showRealTimeErrors) {
            setNewPinError(checkNewPin(newPin));
        }
    }, [newPin, showRealTimeErrors, checkNewPin]);

    const haveValuesChanged = useMemo(() => {
        return newPin !== '';
    }, [newPin]);

    function resetForm() {
        setCurrentPassword('');
        setCurrentPasswordError('');
        setNewPin('');
        setNewPinError('');
        setShowRealTimeErrors(false);
    }

    useImperativeHandle(ref, () => ({
        resetForm() {
            resetForm();
        }
    }));

    const handleSubmit: FormEventHandler = (e) => {
        e.preventDefault();

        setShowRealTimeErrors(true);

        if (!checkCurrentPassword(currentPassword) && !checkNewPin(newPin)) {
            setShowRealTimeErrors(false);
            onSubmit?.({ newPin, currentPassword });
        }
    };

    return (
        <UISection>
            <form onSubmit={handleSubmit} noValidate>
                <UISectionHeader
                    title={t('components.change_pin_section.title')}
                />
                <UISectionContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 24
                        }}
                    >
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_pin_section.current_password_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_pin_section.current_password_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={currentPasswordRef}
                                    name="Current Password"
                                    value={currentPassword}
                                    placeholder={t(
                                        'components.change_pin_section.current_password_placeholder'
                                    )}
                                    type="password"
                                    errorText={currentPasswordError}
                                    required
                                    maxLength={100}
                                    onChange={setCurrentPassword}
                                />
                            </div>
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: 16
                            }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1,
                                    flexDirection: 'column',
                                    gap: 4
                                }}
                            >
                                <UIText>
                                    {t(
                                        'components.change_pin_section.pin_label'
                                    )}
                                </UIText>
                                <UIText type="default2">
                                    {t(
                                        'components.change_pin_section.pin_description'
                                    )}
                                </UIText>
                            </div>

                            <div
                                style={{
                                    display: 'flex',
                                    flex: 1
                                }}
                            >
                                <UIFormInput
                                    ref={newPinRef}
                                    name="PIN"
                                    value={newPin}
                                    placeholder={t(
                                        'components.change_pin_section.pin_placeholder'
                                    )}
                                    type="password"
                                    autoComplete="on"
                                    errorText={newPinError}
                                    required
                                    maxLength={4}
                                    onChange={setNewPin}
                                />
                            </div>
                        </div>
                    </div>
                </UISectionContent>
                {haveValuesChanged && (
                    <UISectionFooter
                        actions={[
                            {
                                variant: 'primary',
                                type: 'submit',
                                loading: loading,
                                disabled: loading,
                                text: loading
                                    ? t('shared.labels.saving')
                                    : t('shared.labels.save')
                            },
                            {
                                text: t('shared.labels.cancel'),
                                disabled: loading,
                                onClick: resetForm
                            }
                        ]}
                    />
                )}
            </form>
        </UISection>
    );
});
